import { HttpClient } from '@angular/common/http';
import {
    computed,
    inject,
    Injectable,
    Signal,
    signal,
    WritableSignal,
} from '@angular/core';
import { DatiqAppConfig } from '@dat/datiq-core/models';
import { DatDbClientService } from '@dat/db';
import { IqTrendlinesSegmentTrackingService } from '@dat/iq-trendlines-ff';
import { isNil } from 'lodash';
import { DateTime } from 'luxon';
import { lastValueFrom } from 'rxjs';
import {
    Col,
    ColumnKeys,
    DataRows,
    emptyDataRow,
    emptyDataRows,
    EquipmentType,
    FlatbedTrends,
    IIqTrendlinesSnapshotStateService,
    NationalTrends,
    ReeferTrends,
    Rows,
    TL_SNAPSHOT_URL,
    TRENDLINES_CACHE_DB,
    TRENDLINES_TABLE_DOC,
    TrendlinesLabelCol,
    TrendlinesSnapshotResponse,
    UpsellColumnKeys,
    UpsellRows,
    UpsellTrackingEventName,
    VanTrends,
    defaultTrendlinesSnapshotResponse,
} from '../../models';

const defaultCol: (id: string) => Col = (id: string) => ({
    id,
    label: '--',
});

const dataCol: (value: string | number) => Col = (value: string | number) => ({
    label: generateLabel(value),
});

const upsellCol: (linkLabel: string, redirectUrl: string) => Col = (
    linkLabel: string,
    redirectUrl: string,
) => ({
    label: 'Available in',
    linkLabel: linkLabel,
    isLink: true,
    isUpsell: true,
    redirectUrl: redirectUrl,
});

type TrendlinesSnapshotResponseCache = {
    data: TrendlinesSnapshotResponse;
    expiryDate: string;
};

@Injectable({
    providedIn: 'root',
})
export class IqTrendlinesSnapshotStateService
    implements IIqTrendlinesSnapshotStateService
{
    private _baseUrl: string =
        this._getAnalyticsUrl() + inject(TL_SNAPSHOT_URL);
    private _db = inject(DatDbClientService);
    private _trackingService = inject(IqTrendlinesSegmentTrackingService);

    private _getAnalyticsUrl(): string {
        return this._appConfig?.coreConfig?.ANALYTICS_BASE_URL ?? '';
    }

    private _getIqBaseUrl(): string {
        return this._appConfig?.coreConfig?.IQ_BASE_URL ?? '';
    }

    private DB_OPTS: { dbName: string } = {
        dbName: TRENDLINES_CACHE_DB,
    };

    private _getMarketConditionsUrl = (
        equipmentType: EquipmentType,
    ): string => {
        return `${this._getIqBaseUrl()}/market-conditions/conditions/${equipmentType}~KMA~FORECAST~OUT~~~7`;
    };

    private _getRateviewUrl = (): string => {
        return `${this._getIqBaseUrl()}/rateview`;
    };

    private PACIFIC_TIME_ZONE: string = 'America/Los_Angeles';
    constructor(
        private _httpClient: HttpClient,
        private _appConfig: DatiqAppConfig,
    ) {
        this._db.createDBs([TRENDLINES_CACHE_DB]);
    }

    private _isDataStale(expiryDate: string): boolean {
        // The Dates are stored in JS Date format, we need to convert them to Pacific Local Time
        const pacificExpiryDate: DateTime = DateTime.fromISO(
            expiryDate,
        ).setZone(this.PACIFIC_TIME_ZONE);
        if (isNil(pacificExpiryDate)) return true;
        const currentPacificTime: DateTime = DateTime.utc().setZone(
            this.PACIFIC_TIME_ZONE,
        );
        return currentPacificTime > pacificExpiryDate;
    }

    private _nextDay(): Date {
        //Next day at 7am
        return DateTime.utc()
            .setZone(this.PACIFIC_TIME_ZONE)
            .plus({ days: 1 })
            .set({
                hour: 7,
                minute: 0,
                second: 0,
                millisecond: 0,
            })
            .toJSDate();
    }

    async data(): Promise<DataRows> {
        const response: TrendlinesSnapshotResponse = await this.getRows();
        const data = this.responseMapper(response);
        return data;
    }

    rows(
        response: TrendlinesSnapshotResponse,
        isUpsellEnabled: boolean,
    ): Signal<Rows> | Signal<UpsellRows> {
        // Use the correct column mapping based on upsell flag
        const mappedRows = this._columnMapper(response);
        if (!isUpsellEnabled) {
            // Remove upsell columns for non-upsell mode
            Object.keys(mappedRows).forEach((key) => {
                if (mappedRows[key]) {
                    delete mappedRows[key].EightDayForecastUpsell;
                    delete mappedRows[key].ThirtyFiveDayForecastUpsell;
                }
            });
        }

        // Use signal for reactivity
        const rowsSig: WritableSignal<Rows> | WritableSignal<UpsellRows> =
            signal(mappedRows as Rows | UpsellRows);

        return computed(() => rowsSig());
    }

    async getRows(): Promise<TrendlinesSnapshotResponse> {
        const hasDoc = await lastValueFrom(
            this._db.hasDoc(TRENDLINES_TABLE_DOC, this.DB_OPTS),
        );
        if (!hasDoc) {
            return this._fetchAndCacheRowData();
        }
        const cachedRows: TrendlinesSnapshotResponseCache =
            (await lastValueFrom(
                this._db.getDoc(TRENDLINES_TABLE_DOC, this.DB_OPTS),
            )) as TrendlinesSnapshotResponseCache;

        if (this._isDataStale(cachedRows.expiryDate)) {
            return this._fetchAndCacheRowData();
        }
        return cachedRows.data;
    }

    private async _fetchAndCacheRowData(): Promise<TrendlinesSnapshotResponse> {
        try {
            const data: TrendlinesSnapshotResponse = await lastValueFrom(
                this._httpClient.get<TrendlinesSnapshotResponse>(this._baseUrl),
            );
            this._cacheRowData(data);
            return data;
        } catch (error) {
            console.error('Error fetching trendlines data from API:', error);
            return defaultTrendlinesSnapshotResponse;
        }
    }

    private _cacheRowData(data: TrendlinesSnapshotResponse): void {
        const doc: TrendlinesSnapshotResponseCache = {
            data,
            expiryDate: this._nextDay().toISOString(),
        };
        this._db.setDoc(TRENDLINES_TABLE_DOC, doc, this.DB_OPTS);
    }

    responseMapper(data: TrendlinesSnapshotResponse): DataRows {
        return {
            ...emptyDataRows,
            SpotLoadPosts: this._mapSpotLoadPosts(data?.nationalTrends),
            SpotTruckPosts: this._mapSpotTruckPosts(data?.nationalTrends),
            VanLoadToTruck: this._mapVanLoadToTruck(data?.vanTrends),
            ReeferLoadToTruck: this._mapReeferLoadToTruck(data?.reeferTrends),
            FlatbedLoadToTruck: this._mapFlatbedLoadToTruck(
                data?.flatbedTrends,
            ),
            VanSpotRates: this._mapVanSpotRates(data?.vanTrends),
            FlatbedSpotRates: this._mapFlatbedSpotRates(data?.flatbedTrends),
            ReeferSpotRates: this._mapReeferSpotRates(data?.reeferTrends),
            FuelPrices: this._mapFuelPrices(data?.nationalTrends),
        };
    }

    private _columnMapper(data: TrendlinesSnapshotResponse): Rows {
        return {
            SpotLoadPosts: this._mapSpotLoadPostsColumn(data?.nationalTrends),
            SpotTruckPosts: this._mapSpotTruckPostsColumn(data?.nationalTrends),
            VanLoadToTruck: this._mapVanLoadToTruckColumn(data?.vanTrends),
            ReeferLoadToTruck: this._mapReeferLoadToTruckColumn(
                data?.reeferTrends,
            ),
            FlatbedLoadToTruck: this._mapFlatbedLoadToTruckColumn(
                data?.flatbedTrends,
            ),
            VanSpotRates: this._mapVanSpotRatesColumn(data?.vanTrends),
            FlatbedSpotRates: this._mapFlatbedSpotRatesColumn(
                data?.flatbedTrends,
            ),
            ReeferSpotRates: this._mapReeferSpotRatesColumn(data?.reeferTrends),
            FuelPrices: this._mapFuelPricesColumn(data?.nationalTrends),
        };
    }

    private _mapSpotLoadPostsColumn(
        nationalTrends: NationalTrends,
    ): Record<UpsellColumnKeys, Col> {
        return {
            Trends: { ...TrendlinesLabelCol?.SpotLoadPosts },
            WeekComparison: {
                ...dataCol(
                    nationalTrends?.weekOverWeekNationalLoadPostingsChangeInPercentage,
                ),
            },
            MonthComparison: {
                ...dataCol(
                    nationalTrends?.monthOverMonthNationalLoadPostingsChangeInPercentage,
                ),
            },
            YearComparison: {
                ...dataCol(
                    nationalTrends?.yearOverYearNationalLoadPostingsChangeInPercentage,
                ),
            },
            EightDayForecastUpsell: {
                ...defaultCol('SpotLoadsEightDayForecastUpsell'),
            },
            ThirtyFiveDayForecastUpsell: {
                ...defaultCol('SpotLoadsThirtyFiveDayForecastUpsell'),
            },
        };
    }

    private _mapSpotTruckPostsColumn(
        nationalTrends: NationalTrends,
    ): Record<UpsellColumnKeys, Col> {
        return {
            Trends: { ...TrendlinesLabelCol?.SpotTruckPosts },
            WeekComparison: {
                ...dataCol(
                    nationalTrends?.weekOverWeekNationalTruckPostingsChangeInPercentage,
                ),
            },
            MonthComparison: {
                ...dataCol(
                    nationalTrends?.monthOverMonthNationalTruckPostingsChangeInPercentage,
                ),
            },
            YearComparison: {
                ...dataCol(
                    nationalTrends?.yearOverYearNationalTruckPostingsChangeInPercentage,
                ),
            },
            EightDayForecastUpsell: {
                ...defaultCol('SpotTrucksEightDayForecastUpsell'),
            },
            ThirtyFiveDayForecastUpsell: {
                ...defaultCol('SpotTrucksThirtyFiveDayForecastUpsell'),
            },
        };
    }

    private _mapVanLoadToTruckColumn(
        vanTrends: VanTrends,
    ): Record<UpsellColumnKeys, Col> {
        return {
            Trends: { ...TrendlinesLabelCol?.VanLoadToTruck },
            WeekComparison: {
                ...dataCol(
                    vanTrends?.weekOverWeekVanLoadToTruckRatioChangeInPercentage,
                ),
            },
            MonthComparison: {
                ...dataCol(
                    vanTrends?.monthOverMonthVanLoadToTruckRatioChangeInPercentage,
                ),
            },
            YearComparison: {
                ...dataCol(
                    vanTrends?.yearOverYearVanLoadToTruckRatioChangeInPercentage,
                ),
            },
            EightDayForecastUpsell: {
                ...upsellCol(
                    'Market Conditions',
                    this._getMarketConditionsUrl('VAN'),
                ),
            },
            ThirtyFiveDayForecastUpsell: {
                ...defaultCol('VanLoadToTruckThirtyFiveDayForecastUpsell'),
            },
        };
    }

    private _mapReeferLoadToTruckColumn(
        reeferTrends: ReeferTrends,
    ): Record<UpsellColumnKeys, Col> {
        return {
            Trends: { ...TrendlinesLabelCol?.ReeferLoadToTruck },
            WeekComparison: {
                ...dataCol(
                    reeferTrends?.weekOverWeekReeferLoadToTruckRatioChangeInPercentage,
                ),
            },
            MonthComparison: {
                ...dataCol(
                    reeferTrends?.monthOverMonthReeferLoadToTruckRatioChangeInPercentage,
                ),
            },
            YearComparison: {
                ...dataCol(
                    reeferTrends?.yearOverYearReeferLoadToTruckRatioChangeInPercentage,
                ),
            },
            EightDayForecastUpsell: {
                ...upsellCol(
                    'Market Conditions',
                    this._getMarketConditionsUrl('REEFER'),
                ),
            },
            ThirtyFiveDayForecastUpsell: {
                ...defaultCol('ReeferLoadToTruckThirtyFiveDayForecastUpsell'),
            },
        };
    }

    private _mapFlatbedLoadToTruckColumn(
        flatbedTrends: FlatbedTrends,
    ): Record<UpsellColumnKeys, Col> {
        return {
            Trends: { ...TrendlinesLabelCol?.FlatbedLoadToTruck },
            WeekComparison: {
                ...dataCol(
                    flatbedTrends?.weekOverWeekFlatbedLoadToTruckRatioChangeInPercentage,
                ),
            },
            MonthComparison: {
                ...dataCol(
                    flatbedTrends?.monthOverMonthFlatbedLoadToTruckRatioChangeInPercentage,
                ),
            },
            YearComparison: {
                ...dataCol(
                    flatbedTrends?.yearOverYearFlatbedLoadToTruckRatioChangeInPercentage,
                ),
            },
            EightDayForecastUpsell: {
                ...upsellCol(
                    'Market Conditions',
                    this._getMarketConditionsUrl('FLATBED'),
                ),
            },
            ThirtyFiveDayForecastUpsell: {
                ...defaultCol('FlatbedLoadToTruckThirtyFiveDayForecastUpsell'),
            },
        };
    }

    private _mapVanSpotRatesColumn(
        vanTrends: VanTrends,
    ): Record<UpsellColumnKeys, Col> {
        return {
            Trends: { ...TrendlinesLabelCol?.VanSpotRates },
            WeekComparison: {
                ...dataCol(
                    vanTrends?.weekOverWeekVanSpotRateChangeInPercentage,
                ),
            },
            MonthComparison: {
                ...dataCol(
                    vanTrends?.monthOverMonthVanSpotRateChangeInPercentage,
                ),
            },
            YearComparison: {
                ...dataCol(
                    vanTrends?.yearOverYearVanSpotRateChangeInPercentage,
                ),
            },
            EightDayForecastUpsell: {
                ...defaultCol('VanSpotRatesEightDayForecastUpsell'),
            },
            ThirtyFiveDayForecastUpsell: {
                ...upsellCol('RateView', this._getRateviewUrl()),
            },
        };
    }

    private _mapFlatbedSpotRatesColumn(
        flatbedTrends: FlatbedTrends,
    ): Record<UpsellColumnKeys, Col> {
        return {
            Trends: { ...TrendlinesLabelCol?.FlatbedSpotRates },
            WeekComparison: {
                ...dataCol(
                    flatbedTrends?.weekOverWeekFlatbedSpotRateChangeInPercentage,
                ),
            },
            MonthComparison: {
                ...dataCol(
                    flatbedTrends?.monthOverMonthFlatbedSpotRateChangeInPercentage,
                ),
            },
            YearComparison: {
                ...dataCol(
                    flatbedTrends?.yearOverYearFlatbedSpotRateChangeInPercentage,
                ),
            },
            EightDayForecastUpsell: {
                ...defaultCol('FlatbedSpotRatesEightDayForecastUpsell'),
            },
            ThirtyFiveDayForecastUpsell: {
                ...upsellCol('RateView', this._getRateviewUrl()),
            },
        };
    }

    private _mapReeferSpotRatesColumn(
        reeferTrends: ReeferTrends,
    ): Record<UpsellColumnKeys, Col> {
        return {
            Trends: { ...TrendlinesLabelCol?.ReeferSpotRates },
            WeekComparison: {
                ...dataCol(
                    reeferTrends?.weekOverWeekReeferSpotRateChangeInPercentage,
                ),
            },
            MonthComparison: {
                ...dataCol(
                    reeferTrends?.monthOverMonthReeferSpotRateChangeInPercentage,
                ),
            },
            YearComparison: {
                ...dataCol(
                    reeferTrends?.yearOverYearReeferSpotRateChangeInPercentage,
                ),
            },
            EightDayForecastUpsell: {
                ...defaultCol('ReeferSpotRatesEightDayForecastUpsell'),
            },
            ThirtyFiveDayForecastUpsell: {
                ...upsellCol('RateView', this._getRateviewUrl()),
            },
        };
    }

    private _mapFuelPricesColumn(
        nationalTrends: NationalTrends,
    ): Record<UpsellColumnKeys, Col> {
        return {
            Trends: { ...TrendlinesLabelCol?.FuelPrices },
            WeekComparison: {
                ...dataCol(
                    nationalTrends?.weekOverWeekNationalFuelPriceChangeInPercentage,
                ),
            },
            MonthComparison: {
                ...dataCol(
                    nationalTrends?.monthOverMonthNationalFuelPriceChangeInPercentage,
                ),
            },
            YearComparison: {
                ...dataCol(
                    nationalTrends?.yearOverYearNationalFuelPriceChangeInPercentage,
                ),
            },
            EightDayForecastUpsell: {
                ...defaultCol('FuelPricesEightDayForecastUpsell'),
            },
            ThirtyFiveDayForecastUpsell: {
                ...defaultCol('FuelPricesThirtyFiveDayForecastUpsell'),
            },
        };
    }

    private _mapSpotLoadPosts(
        nationalTrends: NationalTrends,
    ): Record<ColumnKeys, number | string> {
        return {
            ...emptyDataRow,
            Trends: 0,
            WeekComparison:
                +nationalTrends?.weekOverWeekNationalLoadPostingsChangeInPercentage ||
                0,
            MonthComparison:
                +nationalTrends?.monthOverMonthNationalLoadPostingsChangeInPercentage ||
                0,
            YearComparison:
                +nationalTrends?.yearOverYearNationalLoadPostingsChangeInPercentage ||
                0,
        };
    }

    private _mapSpotTruckPosts(
        nationalTrends: NationalTrends,
    ): Record<ColumnKeys, number | string> {
        return {
            ...emptyDataRow,
            Trends: 0,
            WeekComparison:
                +nationalTrends?.weekOverWeekNationalTruckPostingsChangeInPercentage ||
                0,
            MonthComparison:
                +nationalTrends?.monthOverMonthNationalTruckPostingsChangeInPercentage ||
                0,
            YearComparison:
                +nationalTrends?.yearOverYearNationalTruckPostingsChangeInPercentage ||
                0,
        };
    }

    private _mapVanLoadToTruck(
        vanTrends: VanTrends,
    ): Record<ColumnKeys, number | string> {
        return {
            ...emptyDataRow,
            Trends: 0,
            WeekComparison:
                +vanTrends?.weekOverWeekVanLoadToTruckRatioChangeInPercentage ||
                0,
            MonthComparison:
                +vanTrends?.monthOverMonthVanLoadToTruckRatioChangeInPercentage ||
                0,
            YearComparison:
                +vanTrends?.yearOverYearVanLoadToTruckRatioChangeInPercentage ||
                0,
        };
    }

    private _mapReeferLoadToTruck(
        reeferTrends: ReeferTrends,
    ): Record<ColumnKeys, number | string> {
        return {
            ...emptyDataRow,
            Trends: 0,
            WeekComparison:
                +reeferTrends?.weekOverWeekReeferLoadToTruckRatioChangeInPercentage ||
                0,
            MonthComparison:
                +reeferTrends?.monthOverMonthReeferLoadToTruckRatioChangeInPercentage ||
                0,
            YearComparison:
                +reeferTrends?.yearOverYearReeferLoadToTruckRatioChangeInPercentage ||
                0,
        };
    }

    private _mapFlatbedLoadToTruck(
        flatbedTrends: FlatbedTrends,
    ): Record<ColumnKeys, number | string> {
        return {
            ...emptyDataRow,
            Trends: 0,
            WeekComparison:
                +flatbedTrends?.weekOverWeekFlatbedLoadToTruckRatioChangeInPercentage ||
                0,
            MonthComparison:
                +flatbedTrends?.monthOverMonthFlatbedLoadToTruckRatioChangeInPercentage ||
                0,
            YearComparison:
                +flatbedTrends?.yearOverYearFlatbedLoadToTruckRatioChangeInPercentage ||
                0,
        };
    }

    private _mapVanSpotRates(
        vanTrends: VanTrends,
    ): Record<ColumnKeys, number | string> {
        return {
            ...emptyDataRow,
            Trends: 0,
            WeekComparison:
                +vanTrends?.weekOverWeekVanSpotRateChangeInPercentage || 0,
            MonthComparison:
                +vanTrends?.monthOverMonthVanSpotRateChangeInPercentage || 0,
            YearComparison:
                +vanTrends?.yearOverYearVanSpotRateChangeInPercentage || 0,
        };
    }

    private _mapFlatbedSpotRates(
        flatbedTrends: FlatbedTrends,
    ): Record<ColumnKeys, number | string> {
        return {
            ...emptyDataRow,
            Trends: 0,
            WeekComparison:
                +flatbedTrends?.weekOverWeekFlatbedSpotRateChangeInPercentage ||
                0,
            MonthComparison:
                +flatbedTrends?.monthOverMonthFlatbedSpotRateChangeInPercentage ||
                0,
            YearComparison:
                +flatbedTrends?.yearOverYearFlatbedSpotRateChangeInPercentage ||
                0,
        };
    }

    private _mapReeferSpotRates(
        reeferTrends: ReeferTrends,
    ): Record<ColumnKeys, number | string> {
        return {
            ...emptyDataRow,
            Trends: 0,
            WeekComparison:
                +reeferTrends?.weekOverWeekReeferSpotRateChangeInPercentage ||
                0,
            MonthComparison:
                +reeferTrends?.monthOverMonthReeferSpotRateChangeInPercentage ||
                0,
            YearComparison:
                +reeferTrends?.yearOverYearReeferSpotRateChangeInPercentage ||
                0,
        };
    }

    private _mapFuelPrices(
        nationalTrends: NationalTrends,
    ): Record<ColumnKeys, number | string> {
        return {
            ...emptyDataRow,
            Trends: 0,
            WeekComparison:
                +nationalTrends?.weekOverWeekNationalFuelPriceChangeInPercentage ||
                0,
            MonthComparison:
                +nationalTrends?.monthOverMonthNationalFuelPriceChangeInPercentage ||
                0,
            YearComparison:
                +nationalTrends?.yearOverYearNationalFuelPriceChangeInPercentage ||
                0,
        };
    }

    dipstachUpsellSegmentEvent(upsellApp: string): void {
        const props = {
            page: 'Trucking Industry Trends',
            upsell_app: upsellApp,
            location: 'Upsell table',
        };
        this._trackingService.track(UpsellTrackingEventName, props);
    }
}
function generateLabel(value: string | number): string {
    const numberValue: number = parseFloat(value as string);
    return !isNaN(numberValue)
        ? numberValue >= 0
            ? `+${numberValue.toFixed(1)}%`
            : `${numberValue.toFixed(1)}%`
        : '--';
}
